import isInteger from 'lodash/isInteger';
import isNaN from 'lodash/isNaN';
import isNil from 'lodash/isNil';
import isNumber from 'lodash/isNumber';
import * as R from 'ramda';
import {AvatarUploadInfo} from 'services/UsersAPI';
import turfBooleanWithin from '@turf/boolean-within';
import {LatLng} from 'common/types';
import {toNumber} from 'lodash';
import DateFnsUtils from '@date-io/date-fns';

export type ValidateFunction = (_: any) => undefined | false;

export const required = (value: any) =>
  Array.isArray(value)
    ? value.length
      ? undefined
      : false
    : value
    ? undefined
    : false;

export const charsOnly = (value: any) => {
  return (new RegExp('[^-а-яєіїґ ]+', 'gui')).test(value) ? false : undefined;
};
export const houseNumber = (value: any) => {
  return (new RegExp('^[1-9][0-9]*[ а-я0-9єіїґ\-]*\/?[ а-я0-9]*$', 'gui')).test(value)
    ? undefined
    : false;
};
export const notIntegerValidator = (total_num: number = 0) => (value: any) => {
  if (!value) {
    return undefined;
  }
  const test = value.toString().replace(/[^0-9]/gui, '');
  return test.length > total_num || value.toString().length === test.length ? false : undefined;
};

export const notSymbolsValidator = (symbols: any[]) => (value: any) => {
  if (!value) {
    return undefined;
  }
  const test = value.toString().replace(new RegExp(`[^${symbols.join('')}]+`, 'gui'), '');
  return test.toString().length ? false : undefined;
};

export const number = (value: any) =>
  isNumber(value) && !isNaN(value) ? undefined : false;

export const integer = (value: any) => (isInteger(value) ? undefined : false);

export const autocompleteRequired = (item: any) => {
  if (Array.isArray(item)) {
    return item.length ? undefined : false;
  }

  if (!isNil(item) && !isNil(item.value)) {
    return undefined;
  }
  return false;
};

export const autocompleteRequiredLegacy = (item: any) => {
  if (item) {
    return undefined;
  }
  return false;
};

export const phone = (p: string) => (/\d{9}/.test(p) ? undefined : false);

export const minLength = R.curry((length: number, value: string | number) =>
  String(value).length >= length ? undefined : false
);

export const maxItems = R.curry((count: number, value: any[]) =>
  value.length <= count ? undefined : false
);

export const minValue = R.curry((min: number, value: number) =>
  value >= min ? undefined : false
);

export const maxValue = R.curry((max: number, value: number) =>
  value <= max ? undefined : false
);

export const maxLength = R.curry((length: number, value: string | number) =>
  String(value).length <= length ? undefined : false
);

export const email = (value: string) =>
  // eslint-disable-next-line
  /^(([^<>()\[\]\.,;:\s@\"]+(\.[^<>()\[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i.test(
    value
  )
    ? undefined
    : false;

export const url = (value: string) => {
  const regex = /(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@._\+#=]{1,256}\.[a-z]{2,10}\b([-a-zA-Z0-9@_\+.//?&=]*)/iu;

  const matches = regex.exec(value);
  return matches && matches[0] && matches[0] === value ? undefined : false;
};

export const youtube = (value: string) => {
  if (value.indexOf('youtu') === -1) {
    return false;
  }

  const re = /\/\/(?:www\.)?youtu(?:\.be|be\.com)\/(?:watch\?v=|embed\/)?([a-z0-9_\-]+)/gmi;
  const matches = re.exec(value);
  return matches && matches[1] ? undefined : false;
};

export const youtubeProfile = (value: string) => {
  if (value.indexOf('youtu') === -1) {
    return false;
  }

  const re = /\/\/(?:www\.)?youtu(?:\.be|be\.com)/gmi;
  const test = re.test(value);
  return test ? undefined : false;
};

// Validate video tour url in offer
export const tiktok = (value: string) => {
  if (value.indexOf('tiktok') === -1) {
    return false;
  }

  const re = /(www|vm)\.tiktok\.com.*\/(video\/(\d+)|\w+)/gmi;
  const matches = re.exec(value);
  return matches && matches[1] ? undefined : false;
};

// Validate profile url for Registration & Profile social links
export const commonTiktok = (value: string) => {
  if (value.indexOf('tiktok') === -1) {
    return false;
  }

  const re = /(?:https?:\/\/)?(?:www\.)?tiktok\.com\/@(\w+)/gmi;
  const matches = re.exec(value);
  return matches && matches[1] ? undefined : false;
};

export const instagram = (value: string) => {
  if (value.indexOf('instagr') === -1) {
    return false;
  }

  const re = /(?:(?:http|https):\/\/)?(?:www.)?(?:instagram.com|instagr.am|instagr.com)\/(\w+)/igm;
  const matches = re.exec(value);
  return matches && matches[1] ? undefined : false;
};

export const facebook = (value: string) => {
  if (value.indexOf('facebook') === -1 && value.indexOf('fb') === -1) {
    return false;
  }

  const re = /(?:https?:\/\/)?(?:www\.)?(mbasic.facebook|m\.facebook|facebook|fb)\.(com|me)\/(?:(?:\w\.)*#!\/)?(?:pages\/)?(?:[\w\-\.]*\/)*([\w\-\.]*)/gmi;

  const matches = re.exec(value);
  return matches && matches[1] ? undefined : false;
};

export const videoLink = (value: string) => {
  if (youtube(value) === undefined || tiktok(value) === undefined) {
    return undefined;
  }
  return false;
};

export const cadastralNumber = (value: string) =>
  /^\d{10}:\d{2}:\d{3}:\d{4}$/.test(value) ? undefined : false;

export const inn = (value: string) => {
  const res = /^(\d{5})(\d{3})(\d{1})(\d{1})$/.exec(value);
  if (res) {
    const dr = toNumber(res[1]);
    const num = res[2];
    const control = toNumber(res[4]);
    const sex = res[3];

    const checksum = ((
      -1 * toNumber(value.toString()[0]) +
      5 * toNumber(value.toString()[1]) +
      7 * toNumber(value.toString()[2]) +
      9 * toNumber(value.toString()[3]) +
      4 * toNumber(value.toString()[4]) +
      6 * toNumber(value.toString()[5]) +
      10 * toNumber(value.toString()[6]) +
      5 * toNumber(value.toString()[7]) +
      7 * toNumber(value.toString()[8])
    ) % 11) % 10;

    const innDate = (new DateFnsUtils()).addDays(new Date(1900, 0, 1), dr).getFullYear();

    return checksum === Number(control)
      && innDate > (new Date()).getFullYear() - 100
      ? undefined : false;
  }
  // /^\d{10}:\d{2}:\d{3}:\d{4}$/.test(value) ? undefined : false;
  return undefined;
};

export const personalPhoto = (value: AvatarUploadInfo) =>
  value.isPerson ? undefined : false;

export const isWithinContour = R.curry(
  (geojson: GeoJSON.Feature<GeoJSON.MultiPolygon>, point: LatLng) => {
    return point?.lat &&
      turfBooleanWithin(
        {type: 'Point', coordinates: [point.lng, point.lat]},
        geojson
      )
      ? undefined
      : false;
  }
);

export const textHasUrl = (value: string) => {
  return value && value.match(/[a-z]{2,}\.([a-z]{2,})+/)
    ? false
    : undefined;
};

export const textHasPhone = (value: string) => {
  return value && value.match(/(\+?3?8?\(?0[0-9\-() ]{9,15})/)
    ? false
    : undefined;
};
